import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import toNumber from 'lodash/toNumber';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import { FixedWidthPopupComponent } from '@atlassian/jira-jql-builder-basic-picker/src/ui/popup-container/index.tsx';
import View from '@atlassian/jira-jql-builder-basic-textarea/src/ui/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { NUMBER_SIZE_LIMIT_WHEN_ENABLED } from '../../../../../common/ui/constants.tsx';
import PickerTrigger from '../../../../../common/ui/picker-trigger/main.tsx';
import type { TextAreaCommonProps } from '../../../../../common/ui/types.tsx';
import {
	isUnsubmittedValueNotANumber,
	isUnsubmittedNumberTooLarge,
} from '../../../../../common/ui/utils.tsx';
import {
	useShouldRenderToParent,
	useVerticalMode,
} from '../../../../../controllers/feature-context.tsx';
import { PickerTriggerLayout } from '../../../../../common/ui/picker-trigger-layout/index.tsx';
import messages from './messages.tsx';

export const formatLabel = (label: string, value: string | null, verticalMode = false) => {
	if (value === null || value === undefined || !value.length) {
		return verticalMode ? <PickerTriggerLayout label={label || ''}> </PickerTriggerLayout> : label;
	}

	if (verticalMode) {
		return (
			<PickerTriggerLayout label={label || ''}>
				<ValueWrapper>{value}</ValueWrapper>
			</PickerTriggerLayout>
		);
	}

	return (
		<Flex alignItems="center">
			<Text color="inherit" as="strong">
				{label}
			</Text>
			:&nbsp;<ValueWrapper>{value}</ValueWrapper>
		</Flex>
	);
};

export const NumberPickerPopup = ({
	fieldId,
	fieldDisplayName,
	isOpen,
	onOpen,
	onClose,
	onClear,
	onChange,
	onSendOptionAnalytics,
	value,
	fieldDescription,
	isIrremovable,
}: TextAreaCommonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const shouldRenderToParent = useShouldRenderToParent();
	const verticalMode = useVerticalMode();
	/**
	 * Note - despite the name, this actually handles the onClick
	 * of the 'Update' button that appears in the footer of the picker content,
	 * it does not get called on a change event
	 */
	const handleOnChange = useCallback(
		(inputValue: string) => {
			onChange(inputValue);
			onClose();
			onSendOptionAnalytics &&
				onSendOptionAnalytics({
					searchStringLength: inputValue.length,
				});
		},
		[onChange, onClose, onSendOptionAnalytics],
	);

	const handleOnTriggerClick = useCallback(() => {
		isOpen ? onClose() : onOpen();
	}, [isOpen, onClose, onOpen]);

	const getNumberPickerErrorMessage = useCallback(
		(inputValue: string): string | undefined => {
			if (isUnsubmittedValueNotANumber(inputValue)) {
				return formatMessage(messages.validNumberErrorMessage);
			}
			// this assumes that value is a valid number
			if (isUnsubmittedNumberTooLarge(toNumber(inputValue))) {
				return formatMessage(messages.numberSizeLimitErrorMessage, {
					maxSize: NUMBER_SIZE_LIMIT_WHEN_ENABLED.toLocaleString(),
				});
			}
		},
		[formatMessage],
	);

	return (
		<Popup
			key={fieldId}
			placement="bottom-start"
			isOpen={isOpen}
			onClose={onClose}
			popupComponent={FixedWidthPopupComponent}
			shouldRenderToParent={shouldRenderToParent}
			messageId="jql-builder-basic.ui.jql-editor.field.text-area-picker.number-picker-popup.popup"
			messageType="transactional"
			trigger={({ ref }) => (
				<PickerTrigger
					isInvalid={false}
					isSelected={isOpen}
					hasValues={!!value}
					onClick={handleOnTriggerClick}
					onClear={onClear}
					ref={ref}
					testId={`jql-builder-basic.ui.jql-editor.number.filter-button.${fieldId}`}
					fieldDescription={fieldDescription}
					isIrremovable={isIrremovable}
				>
					{formatLabel(fieldDisplayName, value, verticalMode)}
				</PickerTrigger>
			)}
			content={({ setInitialFocusRef }) => (
				<View
					value={value || ''}
					getErrorMessage={getNumberPickerErrorMessage}
					setInitialFocusRef={setInitialFocusRef}
					multiline={false}
					onChange={handleOnChange}
					placeholderText={formatMessage(messages.numberPlaceholderMessage)}
				/>
			)}
		/>
	);
};

export default NumberPickerPopup;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ValueWrapper = styled.span({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '200px',
});
